import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { serviceLogoSource } from "./serviceLogos";

function ServiceLabel({ service }) {
  const source = serviceLogoSource(service);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [source]);
  return <><span className="meeting-service-option-icon" aria-hidden="true">
    {source && !failed ? <img src={source} alt="" onError={() => setFailed(true)} /> : service.ServiceDisplayName.slice(0, 1)}
  </span><span className="meeting-service-option-name">{service.ServiceDisplayName}</span></>;
}

export default function MeetingServiceSelector({ services, value, onChange, disabled }) {
  const id = useId();
  const root = useRef(null);
  const control = useRef(null);
  const list = useRef(null);
  const typed = useRef({ text: "", at: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState({ above: false, maxHeight: 320, controlHeight: 44 });
  const selectedIndex = services.findIndex(service => String(service.ServiceId) === String(value));
  const current = services[selectedIndex];
  const expanded = open && !disabled && services.length > 0;
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  useEffect(() => {
    if (!expanded) return;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [expanded]);
  useLayoutEffect(() => {
    if (!expanded) return;
    const position = () => {
      const rect = control.current.getBoundingClientRect();
      const below = Math.max(0, window.innerHeight - rect.bottom - 12);
      const above = Math.max(0, rect.top - 12);
      const openAbove = below < Math.min(320, list.current.scrollHeight) && above > below;
      setPlacement({ above: openAbove, maxHeight: Math.min(320, openAbove ? above : below), controlHeight: rect.height });
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [expanded, services.length]);
  useEffect(() => {
    if (!expanded) return;
    const menu = list.current;
    const option = menu.querySelector(`[data-option-index="${Math.min(active, services.length - 1)}"]`);
    if (!option) return;
    if (option.offsetTop < menu.scrollTop) menu.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > menu.scrollTop + menu.clientHeight)
      menu.scrollTop = option.offsetTop + option.offsetHeight - menu.clientHeight;
  }, [active, expanded, placement.maxHeight, services.length]);
  function show() {
    if (disabled || !services.length) return;
    setActive(Math.max(0, selectedIndex)); typed.current = { text: "", at: 0 }; setOpen(true);
  }
  function choose(index) {
    if (!disabled && services[index]) onChange(String(services[index].ServiceId));
    setOpen(false);
  }
  function keyDown(event) {
    const { key } = event;
    if (key === "Escape" || key === "Tab") {
      setOpen(false); if (key === "Escape") event.preventDefault(); return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(key)) {
      event.preventDefault();
      if (!expanded) show();
      setActive(index => key === "Home" ? 0 : key === "End" ? services.length - 1
        : !expanded ? Math.max(0, selectedIndex) : Math.max(0, Math.min(services.length - 1, index + (key === "ArrowDown" ? 1 : -1))));
    } else if (key === "Enter" || key === " ") {
      event.preventDefault(); if (expanded) choose(Math.min(active, services.length - 1)); else show();
    } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault(); if (!expanded) show();
      const now = Date.now();
      const text = (now - typed.current.at < 700 ? typed.current.text : "") + key.toLocaleLowerCase();
      typed.current = { text, at: now };
      const match = services.findIndex(service => service.ServiceDisplayName.toLocaleLowerCase().startsWith(text));
      if (match >= 0) setActive(match);
    }
  }
  return <div className="meeting-service-selector" ref={root}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <label id={`${id}-label`} htmlFor={`${id}-control`}>Service</label>
    <button ref={control} id={`${id}-control`} type="button" role="combobox" aria-labelledby={`${id}-label`}
      aria-haspopup="listbox" aria-expanded={expanded} aria-controls={`${id}-list`} aria-required="true" disabled={disabled || !services.length}
      aria-activedescendant={expanded && services.length ? `${id}-option-${Math.min(active, services.length - 1)}` : undefined}
      className="meeting-service-selector-control" onKeyDown={keyDown}
      onClick={() => { if (expanded) setOpen(false); else show(); }}>
      {current ? <ServiceLabel key={current.ServiceId} service={current} /> : <span>Choose a service</span>}
      <span className="meeting-service-selector-arrow" aria-hidden="true">▾</span>
    </button>
    {expanded && <ul ref={list} id={`${id}-list`} role="listbox" aria-labelledby={`${id}-label`} className="meeting-service-selector-options"
      style={{ maxHeight: placement.maxHeight, ...(placement.above ? { top: "auto", bottom: placement.controlHeight + 4, marginTop: 0 } : {}) }}>
      {services.map((service, index) => <li key={service.ServiceId} id={`${id}-option-${index}`} role="option"
        aria-selected={String(service.ServiceId) === String(value)} aria-label={service.ServiceDisplayName}
        data-option-index={index} className={active === index ? "is-active" : undefined}
        onMouseDown={event => event.preventDefault()} onMouseMove={() => setActive(index)} onClick={() => choose(index)}>
        <ServiceLabel service={service} />
      </li>)}
    </ul>}
  </div>;
}
